import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

// Story 79.3 — Sequence enrollment panel for AccountDetailPage.
//
// IMPORTANT: This panel records enrollment INTENT only. Actual sequence
// execution is deferred to Epic 61 (sequence engine). See
// AccountSequenceEnrollmentService in the backend for the full comment.

interface EnrollmentSummary {
  id: string;
  accountId: string;
  sequenceRef: string;
  status: string;
  enrolledAt: string;
}

interface PreviewContact {
  contactId: string;
  firstName: string;
  lastName: string;
  email: string | null;
}

interface PreviewGroup {
  buyingRole: string;
  channelHint: string;
  contacts: PreviewContact[];
}

interface EnrollmentPreview {
  accountId: string;
  totalContacts: number;
  groups: PreviewGroup[];
}

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-900/40 text-amber-200',
  ACTIVE: 'bg-emerald-900/40 text-emerald-200',
  PAUSED: 'bg-blue-900/40 text-blue-200',
  COMPLETED: 'bg-zinc-800/60 text-muted-foreground',
  CANCELLED: 'bg-zinc-800/60 text-muted-foreground',
};

export function SequenceEnrollmentPanel({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const [enrollments, setEnrollments] = useState<EnrollmentSummary[]>([]);
  const [showEnroll, setShowEnroll] = useState(false);
  const [preview, setPreview] = useState<EnrollmentPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sequenceRef, setSequenceRef] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadEnrollments = useCallback(async () => {
    try {
      const { data } = await apiClient.get<EnrollmentSummary[]>(
        `/accounts/${accountId}/sequence-enrollments`,
      );
      setEnrollments(data);
    } catch {
      setEnrollments([]);
    }
  }, [accountId]);

  useEffect(() => {
    loadEnrollments();
  }, [loadEnrollments]);

  async function openEnroll() {
    setShowEnroll(true);
    setPreview(null);
    setError(null);
    setLoadingPreview(true);
    try {
      const { data } = await apiClient.get<EnrollmentPreview>(
        `/accounts/${accountId}/sequence-enrollments/preview`,
      );
      setPreview(data);
    } catch {
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }

  async function submit() {
    if (!sequenceRef.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post(`/accounts/${accountId}/sequence-enrollments`, {
        sequenceRef: sequenceRef.trim(),
        notes: notes.trim() || null,
      });
      setShowEnroll(false);
      setSequenceRef('');
      setNotes('');
      await loadEnrollments();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? t('sequenceEnrollment.alreadyEnrolled'));
    } finally {
      setSubmitting(false);
    }
  }

  async function cancelEnrollment(id: string) {
    if (!confirm(t('sequenceEnrollment.confirmCancel'))) return;
    try {
      await apiClient.delete(`/accounts/${accountId}/sequence-enrollments/${id}`);
      await loadEnrollments();
    } catch {
      // ignore
    }
  }

  return (
    <div className="rounded-lg border bg-card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('sequenceEnrollment.title')}
        </h2>
        <Button size="sm" onClick={openEnroll}>
          {t('sequenceEnrollment.enroll')}
        </Button>
      </div>

      {/* Epic 61 note */}
      <p className="text-xs text-muted-foreground italic">
        {t('sequenceEnrollment.epic61Note')}
      </p>

      {/* Enroll dialog */}
      {showEnroll && (
        <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
          <Input
            placeholder={t('sequenceEnrollment.sequenceRef')}
            value={sequenceRef}
            onChange={(e) => setSequenceRef(e.target.value)}
          />
          <Input
            placeholder={t('sequenceEnrollment.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {/* Preview */}
          {loadingPreview && (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          )}
          {preview && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('sequenceEnrollment.previewTitle')} ({preview.totalContacts})
              </p>
              <div className="space-y-1.5">
                {preview.groups.map((g) => (
                  <div key={g.buyingRole} className="rounded-md bg-background border px-3 py-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium">{t(`buyingRole.${g.buyingRole}`)}</span>
                      <span className="text-muted-foreground">{g.channelHint}</span>
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {g.contacts.map((c) => (
                        <li key={c.contactId} className="text-xs text-muted-foreground">
                          {c.firstName} {c.lastName}
                          {c.email ? ` · ${c.email}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setShowEnroll(false); setError(null); }}
              disabled={submitting}
            >
              {t('sequenceEnrollment.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={submitting || !sequenceRef.trim()}
            >
              {t('sequenceEnrollment.enrollButton')}
            </Button>
          </div>
        </div>
      )}

      {/* Existing enrollments */}
      {enrollments.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('sequenceEnrollment.noEnrollments')}</p>
      ) : (
        <ul className="divide-y">
          {enrollments.map((e) => (
            <li key={e.id} className="flex items-center justify-between py-2">
              <div>
                <div className="font-medium text-sm">{e.sequenceRef}</div>
                <div className="text-xs text-muted-foreground">
                  {t('sequenceEnrollment.enrolledAt')}:{' '}
                  {new Date(e.enrolledAt).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    STATUS_COLORS[e.status] ?? 'bg-muted text-foreground'
                  }`}
                >
                  {t(`sequenceEnrollment.${e.status}`)}
                </span>
                {e.status === 'PENDING' && (
                  <button
                    onClick={() => cancelEnrollment(e.id)}
                    className="text-xs text-destructive/70 hover:text-destructive"
                  >
                    {t('sequenceEnrollment.cancelEnrollment')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
